/**
 * Resolving alarm threshold keys from what the server sends.
 *
 * The browser used to derive the storage key prefix itself:
 *
 *     const prefix = ampType === 'velocity' ? '' : `${ampType}_`;
 *
 * repeated at four call sites in OverallAlarmModal.js. That expression is the
 * reason a new measure could not be edited here even once the backend accepted
 * its key: the browser had no way to learn a prefix nobody had hardcoded.
 *
 * The tag response now carries `alarm_parameters`, one entry per measure with
 * its threshold_prefix and the two composed keys. These helpers read that, and
 * fall back to the old derivation when the field is absent so an older backend
 * keeps working.
 */
import _ from 'lodash';
import { BASE_GRAPH_OPTIONS } from '../constants/overallAlarm.constants';

const LEGACY_VELOCITY = 'velocity';

// Contract with the backend: an empty alarm_parameters means no measure is
// seeded for this tag type, and is read like an absent field - historic rules.
export const hasAlarmParameters = tag => Boolean(
  tag && tag.alarm_parameters && tag.alarm_parameters.length
);

/**
 * The threshold_prefix for an amplitude type, e.g. '' or 'acceleration'.
 * Note this is the prefix WITHOUT the trailing underscore -- the same form the
 * server stores in parameter_metadata.threshold_prefix.
 */
export const legacyPrefixFor = ampType => (
  ampType === LEGACY_VELOCITY ? '' : ampType
);

/**
 * The alarm_parameters entry for an amplitude type, or null.
 *
 * Matched on threshold_prefix rather than on amplitude_type, because the UI's
 * three tokens are not amplitude types: 'demod' and 'acceleration' share the
 * amplitude type 'acceleration' and differ by feature. The prefix is what
 * distinguishes them, and it is what the keys are built from.
 */
export const findAlarmParameter = (tag, ampType) => {
  if (!hasAlarmParameters(tag)) return null;

  const wanted = legacyPrefixFor(ampType);
  const match = tag.alarm_parameters.filter(
    parameter => parameter.threshold_prefix === wanted
  );

  return match.length ? match[0] : null;
};

/**
 * The measurement column a chart is showing, from its two separate controls.
 *
 * The chart splits what parameter_metadata keeps as one string: a feature
 * ('rms', 'peak', 'p2p') and an amplitude ('velocity', 'acceleration',
 * 'demod'). Joining them gives the metadata's `feature` - 'peak_velocity' -
 * which is the only value that identifies a measure once one name can belong
 * to two amplitudes.
 *
 * 'demod' is not an amplitude type; it is a prefix over acceleration, and the
 * column it names is spelled out in full.
 */
export const measurementFeatureName = (chartFeature, ampType) => {
  if (!chartFeature) return null;

  // A tag type with no amplitude - current, temperature - stores the feature
  // name on its own. The charts use 'normal' where vibration uses an
  // amplitude, so both mean "there is no amplitude here".
  if (!ampType || ampType === 'normal') return chartFeature;

  if (ampType === 'demod') return `${chartFeature}_demod_acceleration`;

  return `${chartFeature}_${ampType}`;
};

/**
 * The alarm_parameters entry for exactly what the chart is showing, or null.
 *
 * findAlarmParameter above matches on the amplitude alone, which was enough
 * while each amplitude had one measure. It is not enough now: RMS velocity and
 * peak velocity share a prefix, so matching by amplitude returns whichever the
 * server listed first and a peak chart would read the RMS alarm.
 */
export const findAlarmParameterForFeature = (tag, ampType, chartFeature) => {
  if (!hasAlarmParameters(tag)) return null;

  // A tag type whose measures carry no amplitude type keeps the feature name
  // on its own. Asking the chart is not enough: it holds an amplitude for
  // these tag types too - 'velocity', carried over from whichever vibration
  // tag was open before, and written into the URL - so joining it in would
  // look for a column that cannot exist. The metadata rows are the authority
  // on whether this tag type has amplitudes at all.
  const hasAmplitude = tag.alarm_parameters.some(
    parameter => parameter.amplitude_type
  );
  const wanted = hasAmplitude
    ? measurementFeatureName(chartFeature, ampType)
    : chartFeature;
  if (!wanted) return null;

  const match = tag.alarm_parameters.filter(
    parameter => parameter.feature === wanted
  );
  if (match.length) return match[0];

  // A narrowband band keeps its bare name in the metadata - shaft_speed_1x -
  // and carries the amplitude in its own column, one row per amplitude. The
  // chart shows it under the same bare name, so the joined form above never
  // matches; the pair does.
  const band = tag.alarm_parameters.filter(
    parameter => parameter.feature === chartFeature
      && parameter.amplitude_type === ampType
  );

  return band.length ? band[0] : null;
};

/**
 * The stored limit a chart should draw for exactly the measure it is showing.
 *
 * Returns `owns: false` when that measure has no alarm parameter, which is the
 * caller's signal to fall back to whatever line it drew historically. When the
 * measure does own a key the value is authoritative, including a null: drawing
 * a neighbouring measure's limit instead would show a line for a limit nobody
 * set, which on screen is indistinguishable from one they did.
 *
 * `scope` is the object the limits live in - one speed bin for vibration, the
 * whole `alarm_thresholds` for every other tag type. Which of the two it is
 * stays with the caller, because only the caller knows its bin.
 */
export const ownedThreshold = (tag, ampType, feature, severity, scope) => {
  const parameter = findAlarmParameterForFeature(tag, ampType, feature);
  if (!parameter) return { owns: false, value: null };

  const key = severity === 'critical'
    ? parameter.critical_key
    : parameter.warning_key;
  const value = key && scope ? scope[key] : undefined;

  return { owns: true, value: value === undefined ? null : value };
};

/**
 * The stored key for one field of one measure, e.g. 'acceleration_warning'.
 *
 * Prefers the server's own composed key for warning and critical, so those two
 * are never rebuilt here. Other fields -- base_graph, threshold,
 * lower_outlier_limit -- are composed from the server's prefix, which is still
 * one place rather than four.
 */
export const alarmKey = (tag, ampType, field, feature) => {
  // With a feature, ask for that exact measure: once a tag type carries more
  // than one measure per amplitude, the amplitude alone resolves to whichever
  // the server listed first, and a peak threshold would be written into the
  // RMS slot. Without one, the historic amplitude-only lookup stands.
  const parameter = feature
    ? findAlarmParameterForFeature(tag, ampType, feature)
    : findAlarmParameter(tag, ampType);

  if (parameter) {
    if (field === 'warning' && parameter.warning_key) return parameter.warning_key;
    if (field === 'critical' && parameter.critical_key) return parameter.critical_key;

    return parameter.threshold_prefix
      ? `${parameter.threshold_prefix}_${field}`
      : field;
  }

  const prefix = legacyPrefixFor(ampType);

  return prefix ? `${prefix}_${field}` : field;
};

/**
 * True when the tag lists its alarm measures and this measure is not among them.
 *
 * The chart used to derive a line for such a measure from the amplitude's RMS
 * limit - root 2 for peak, 2 for peak-to-peak. That line looks like an alarm
 * and is not one: no alarm parameter exists behind it, so nothing evaluates it and
 * it can never raise an event. Where this is true the chart draws no line and
 * says why instead.
 *
 * Both a backend that sends no (or empty) alarm_parameters and a chart that names no
 * feature answer false, so the line each of them has always drawn stands.
 */
export const measureHasNoAlarmParameter = (tag, ampType, feature) => Boolean(
  hasAlarmParameters(tag) && feature
  && !findAlarmParameterForFeature(tag, ampType, feature)
);

/**
 * The key one field of one measure is stored under, on a flat tag type.
 *
 * Flat tag types - every one but vibration - keep all their measures' limits
 * in a single object, exactly as vibration keeps a speed bin. The difference
 * is the fallback: a flat measure with no metadata row keeps the bare field
 * name, because that is what it has always stored under. Composing a prefix
 * from ampType here, as alarmKey does for vibration, would rename it.
 */
export const flatMeasureKey = (tag, ampType, feature, field) => (
  findAlarmParameterForFeature(tag, ampType, feature)
    ? alarmKey(tag, ampType, field, feature)
    : field
);

/**
 * One measure's fields, read out of a flat tag type's stored object.
 *
 * Returns only the fields that measure stores. Handing back the whole object
 * filled a second measure's form with the first measure's numbers.
 */
export const readFlatMeasure = (tag, ampType, feature, fields, stored) => {
  const values = {};
  if (!stored) return values;

  fields.forEach((field) => {
    const key = flatMeasureKey(tag, ampType, feature, field);
    if (key in stored) values[field] = stored[key];
  });

  return values;
};

/**
 * One measure's edited fields, written into a flat tag type's stored object.
 *
 * The sibling measures' keys are carried through untouched. Sending the edited
 * values on their own erased them, because they went under the bare field
 * names that the tag type's first measure owns.
 */
export const writeFlatMeasure = (tag, ampType, feature, values, stored) => {
  const merged = Object.assign({}, stored);

  Object.keys(values).forEach((field) => {
    merged[flatMeasureKey(tag, ampType, feature, field)] = values[field];
  });

  return merged;
};

/** One measure's fields removed from a flat tag type's stored object. */
export const dropFlatMeasure = (tag, ampType, feature, fields, stored) => {
  if (!stored) return stored;

  const kept = Object.assign({}, stored);
  fields.forEach((field) => {
    delete kept[flatMeasureKey(tag, ampType, feature, field)];
  });

  return kept;
};

/**
 * The stored object after one measure's edit, ready to send.
 *
 * `edited` is the modal's state for the measure: `{bin: {field: value}}`,
 * where a flat tag type uses the single bin 0. `fields` names the fields this
 * measure owns in the object - the threshold pair or the three settings.
 *
 * Only this measure's keys change: they are removed from every bin and the
 * edited bins put them back. Everything another measure stores is carried
 * through untouched. Sending null when the edited measure is empty erased the
 * whole column, and with it every sibling's automatic alarm - see the vibration
 * case where a manual acceleration alarm nulled the velocity settings.
 *
 * `onlyEditedBins` is for "Enable for similar tags": `edited` holds only the
 * ticked bins, so a bin missing from it is left as stored, not cleared.
 *
 * Returns null only when nothing is left to store at all.
 */
export const mergeMeasureIntoStored = (
  tag, ampType, feature, fields, edited, stored, onlyEditedBins = false
) => {
  const keyFor = field => (
    tag && tag.type === 'vibration'
      ? alarmKey(tag, ampType, field, feature)
      : flatMeasureKey(tag, ampType, feature, field)
  );
  const writesNothing = !edited || !Object.keys(edited).length;
  if (onlyEditedBins && writesNothing) return stored || null;

  if (!tag || tag.type !== 'vibration') {
    let flat = dropFlatMeasure(tag, ampType, feature, fields, stored) || {};
    const values = edited && edited[0] ? edited[0] : {};
    flat = writeFlatMeasure(tag, ampType, feature, values, flat);

    return Object.keys(flat).length ? flat : null;
  }

  const merged = {};
  Object.keys(stored || {}).forEach((bin) => {
    const kept = Object.assign({}, stored[bin]);
    const written = `${bin}`.replace('bin_', '') in (edited || {});
    if (!onlyEditedBins || written) fields.forEach((field) => { delete kept[keyFor(field)]; });
    if (Object.keys(kept).length) merged[bin] = kept;
  });

  Object.keys(edited || {}).forEach((bin) => {
    const binKey = `bin_${bin}`;
    merged[binKey] = merged[binKey] || {};
    Object.keys(edited[bin]).forEach((field) => {
      merged[binKey][keyFor(field)] = edited[bin][field];
    });
  });

  return Object.keys(merged).length ? merged : null;
};

/**
 * The tag PUT body for one measure's overall alarm edit.
 *
 * `onlyEditedBins` is set by "Enable for similar tags" - see mergeMeasureIntoStored.
 */
export const tagAlarmUpdateParams = ({
  tag, ampType, feature, settings, thresholds, storedSettings, storedThresholds, onlyEditedBins = false
}) => {
  const completeThresholds = _.pickBy(thresholds, t => t && t.critical && t.warning);
  const preparedSettings = _.mapValues(_.cloneDeep(settings), (value) => {
    if (value.base_graph.type === BASE_GRAPH_OPTIONS.BASELINE) value.base_graph.value = null;
    return value;
  });

  return {
    overall_alarm_settings: mergeMeasureIntoStored(
      tag, ampType, feature, ['base_graph', 'threshold', 'lower_outlier_limit'],
      preparedSettings, storedSettings, onlyEditedBins
    ),
    alarm_thresholds: mergeMeasureIntoStored(
      tag, ampType, feature, ['warning', 'critical'],
      completeThresholds, storedThresholds, onlyEditedBins
    )
  };
};

/**
 * The calculator settings a save may send.
 *
 * Opening the form seeds a calculator setup on every bin, and typing a manual
 * limit clears it only for that bin. Saved from Manual, the seeds left on rows
 * the user never filled in would be stored as an automatic alarm nobody set up.
 * Keep a bin's settings only when they were already stored or the bin has both
 * limits. Automatic mode sends what the user configured, as before.
 */
export const settingsForSave = ({
  alarmMode, settings, thresholds, storedSettings
}) => {
  if (alarmMode !== 'manual') return settings;

  return _.pickBy(settings, (value, bin) => {
    const limits = thresholds && thresholds[bin];
    return (limits && limits.warning && limits.critical)
      || _.isEqual(value, (storedSettings || {})[bin]);
  });
};

/**
 * The trend request behind the overall alarm form: its graph, its automatic
 * limits and the similar-tags preview.
 *
 * The request has to name the measure. TagTrendDataSchema declares feature with
 * missing=RMS, so leaving it out does not mean "the one on screen" - it means
 * RMS. An automatic threshold for peak velocity was therefore computed from the
 * RMS series and came out about 1.9x too low.
 *
 * Peak alarms are evaluated on the measured peak, whatever the account shows on
 * its charts, so their limits are computed from the measured peak too. A
 * derived-peak limit is sqrt(2) x RMS and the measured peak would sit above it.
 */
export const alarmTrendParams = ({ alarmSettings, ampType, feature }) => {
  const { base_graph: { type, value }, lower_outlier_limit } = alarmSettings;
  const params = { base_graph_type: type };
  if (value) params.base_graph_type_val = value;

  if (ampType === 'demod') {
    params.feature = `${feature || 'rms'}_demod`;
    params.amp_type = 'acceleration';
  } else {
    if (feature) params.feature = feature;
    if (ampType === 'acceleration') params.amp_type = 'acceleration';
  }
  if (params.feature === 'peak') params.peak_preference = 'true_peak';

  if (parseFloat(lower_outlier_limit)) params.lower_outlier_limit = parseFloat(lower_outlier_limit);
  return params;
};

/**
 * Whether the chart behind an open overall alarm form plots the measured peak.
 *
 * The form computes peak limits from the measured peak (alarmTrendParams). On a
 * derived-peak account the chart would otherwise draw sqrt(2) x RMS under those
 * lines. Only that case changes, so every other chart sends what it always did.
 */
export const measuredPeakForAlarmForm = ({
  openOverallAlarm, feature, tag, account
}) => Boolean(
  openOverallAlarm && feature === 'peak' && tag && tag.type === 'vibration'
  && account && account.preferences
  && account.preferences.vibration_peak_preference === 'derived_peak'
);

const LEGACY_VIBRATION_MEASURES = [
  { threshold_prefix: '', amplitude_type: 'velocity', feature: 'rms_velocity' },
  { threshold_prefix: 'demod', amplitude_type: 'acceleration', feature: 'rms_demod_acceleration' },
  { threshold_prefix: 'acceleration', amplitude_type: 'acceleration', feature: 'rms_acceleration' }
];

// Key prefixes ('' or 'acceleration_') the delete dialog's ticked boxes remove
// on a vibration tag. Demod is its own box; every other measure goes by amplitude.
export const deleteAlarmPrefixes = (tag, ticked) => {
  const measures = hasAlarmParameters(tag) ? tag.alarm_parameters : LEGACY_VIBRATION_MEASURES;
  const prefixes = [];
  measures.forEach((measure) => {
    const isDemod = String(measure.feature || '').indexOf('demod') !== -1;
    if (isDemod ? ticked.demod : ticked[measure.amplitude_type]) {
      prefixes.push(measure.threshold_prefix ? `${measure.threshold_prefix}_` : '');
    }
  });

  return prefixes;
};

// A measure with nothing stored opens the way the whole tag used to decide it.
export const initialAlarmMode = ({
  tag, automaticSupported, measureSettings, measureThresholds
}) => {
  if (!automaticSupported) return 'manual';
  if (!_.isEmpty(measureSettings)) return 'automatic';
  if (!_.isEmpty(measureThresholds)) return 'manual';

  return tag.alarm_thresholds && !tag.overall_alarm_settings ? 'manual' : 'automatic';
};

// The overall alarm form shows one measure's slice of the tag's stored settings.
export const storedAlarmInputsChanged = (prevProps, props) => (
  !_.isEqual(prevProps.tag.overall_alarm_settings, props.tag.overall_alarm_settings)
  || !_.isEqual(prevProps.tag.alarm_thresholds, props.tag.alarm_thresholds)
  || prevProps.feature !== props.feature
  || prevProps.ampType !== props.ampType
);
